import {
  alias,
  custom,
  deserialize,
  object,
  optional,
  serializable,
  SKIP,
} from 'serializr';
import { SubscriptionPlan } from '../enums/subscription-plan.enum';
import { SubscriptionStatus } from '../enums/subscription-status.enum';
import dayjs from 'dayjs';
import { BillingAddress } from './billing-address.model';
import { Base } from './base.model';
import { Deserializable } from './deserializable';
import { UsedExecutionTimeModel } from './used-execution-time-model.model';

export class Subscription extends Base implements Deserializable {
  @serializable(alias('total_parallel_runs'))
  public totalParallelRuns: number;
  @serializable
  public freeAdditionalUsers: number;
  @serializable(alias('next_renewal_at'))
  public nextRenewalAt: Date;
  @serializable(object(UsedExecutionTimeModel))
  public usedExecutionTime: UsedExecutionTimeModel;
  @serializable(optional(object(BillingAddress)))
  public billingAddress: BillingAddress;
  @serializable
  public cloudAutomatedMinutesPerMonth: number;
  @serializable
  public localAutomatedMinutesPerMonth: number;
  @serializable
  public allowedUsers: number;
  @serializable
  public status: SubscriptionStatus;
  @serializable
  public plan: SubscriptionPlan;
  @serializable
  public allowedQueueSize: number;
  @serializable
  public freeLocalOnlyMobileSessions: number;
  @serializable
  public additionalParallelRunsOnCloud: number;
  @serializable
  public additionalParallelRunsOnLocal: number;
  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return dayjs(v);
      },
    ),
  )
  public trialEndDate: Date;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(Subscription, input));
  }

  get getPlanName() {
    return this.isFree
      ? 'Free'
      : this.isPro
      ? 'Pro'
      : 'Enterprise';
  }
  get isInTrial() {
    return (
      this.status == SubscriptionStatus.IN_TRIAL
    );
  }

  get isInTrailStatus() {
    return this.status == SubscriptionStatus.IN_TRIAL;
  }

  get isActive() {
    return (
      this.status == SubscriptionStatus.ACTIVE
    );
  }
  get isCancelled() {
    return (
      this.status == SubscriptionStatus.CANCELLED
    );
  }
  get isPaused() {
    return (
      this.status == SubscriptionStatus.PAUSED
    );
  }
  get totalMinutes() {
    return (
      this.cloudAutomatedMinutesPerMonth + this.localAutomatedMinutesPerMonth
    );
  }
  get remainingMinutes() {
    return this.remainingLocalMinutes + this.remainingCloudMinutes;
  }
  get remainingLocalMinutes() {
    const minutes = Math.round(
      this.localAutomatedMinutesPerMonth -
        this.usedExecutionTime?.localDeviceTime / 60,
    );
    return minutes > 0 ? minutes : 0;
  }

  get remainingCloudMinutes() {
    const minutes = Math.round(
      this.cloudAutomatedMinutesPerMonth -
        this.usedExecutionTime?.testsigmaLabTime / 60,
    );
    return minutes > 0 ? minutes : 0;
  }

  get isFree() {
    return this.plan == SubscriptionPlan.Free;
  }
  get isBasic() {
    return this.plan == SubscriptionPlan.Basic;
  }
  
  get isPro() {
    return (
      this.plan == SubscriptionPlan.Pro
    );
  }
  get isEnterprisePlan() {
    return (
      this.plan == SubscriptionPlan.Enterprise
    );
  }

  get endDays() {
    const endDate = dayjs(this.nextRenewalAt);
    const now = dayjs();
    return endDate.diff(now, 'days');
  }

  get ShowExpiry() {
    return this.isInTrial && this.endDays <= 10;
  }

  get statusText() {
    if (this.isInTrial) {
      return 'billing.current_plan.in_trial';
    } else if (this.isPaused) {
      return 'billing.current_plan.paused';
    } else if (this.isCancelled) {
      return 'billing.current_plan.cancelled';
    } else {
      return 'billing.current_plan.active';
    }
  }
  get totalAllowedUsers() {
    return this.allowedUsers + this.freeAdditionalUsers;
  }

  get canShowRemainingMinutes() {
    return (
      (this?.remainingMinutes > 0 && !this?.isFree && !this?.isInTrial) ||
      this?.localAutomatedMinutesPerMonth == -1 ||
      this?.cloudAutomatedMinutesPerMonth == -1
    );
  }
}
